import { Dayjs } from 'dayjs'
import type { EventModelAsStudent } from 'front-end/src/graphql/private/PrivateSchemaTypes'

import dayjs from 'shared/dayjs'
import { isIE } from 'shared/helpers'

export type EventForTimeFormatter = Pick<
  EventModelAsStudent,
  'start_time' | 'end_time' | 'timezone' | 'id'
>

/**
 * These timezones cause Invalid range error at server side rendering for Node 12.
 * Broken timezones has no countries in its models according to Timezone model
 * from https://github.com/manuelmhtr/countries-and-timezones#timezone.
 * Fallback zones was picked randomly according utcOffset and dstOffset.
 * That offset same as broken timezones offsets.
 * Demo with investigation code: https://codesandbox.io/s/condescending-dawn-b7qwg?file=/src/index.js
 * Fallback timezones won't affect on time shift. Result for user will be same.
 */
// TODO: Remove it after Node update
const BROKEN_TIMEZONES_FALLBACK = {
  CET: 'Europe/Berlin',
  CST6CDT: 'America/Chicago',
  EET: 'Europe/Kiev',
  EST: 'America/Jamaica',
  EST5EDT: 'America/New_York',
  Factory: 'Etc/GMT',
  HST: 'Etc/GMT+10',
  MET: 'Europe/Oslo',
  MST: 'Etc/GMT+7',
  MST7MDT: 'America/Denver',
  PST8PDT: 'America/Los_Angeles',
  WET: 'Europe/London'
}

export const createTzDateWithFallback = (
  date: Dayjs | string,
  timezone: string
) => {
  const fallbackTimezone = BROKEN_TIMEZONES_FALLBACK[timezone] || timezone
  return isIE ? dayjs(date) : dayjs.utc(date).tz(fallbackTimezone) // this throws an error like "Option value 'EUROPE/LONDON' for 'timeZone' is outside of valid range. Expected: ['UTC']" on IE
}

export const formatTimezone = (tzString) =>
  `(GMT${createTzDateWithFallback(dayjs(), tzString).format('Z')}) ${tzString}`

/**
 * this does not work in IE11 by design. We render it on the server as workaround. Polyfill https://formatjs.io/docs/polyfills/intl-datetimeformat breaks the bundle and the other polyfill doesn't bundle. See https://github.com/iamkun/dayjs/issues/1497 for more info
 */
export default function eventDateRangeFormatter(
  event: EventForTimeFormatter,
  timezone = event.timezone ?? 'Europe/London', // default timezone from Event for email (no user timezone)
  locale: string = 'enGb'
) {
  const start = createTzDateWithFallback(event.start_time!, timezone)
  const end = event.end_time
    ? createTzDateWithFallback(event.end_time, timezone)
    : null

  const formatSingleDate = function (date: Dayjs) {
    return date.locale(locale).format('LLLL')
  }

  if (!start && !end) {
    return ''
  }

  if (start && !end) {
    return formatSingleDate(start)
  }

  if (!start && end) {
    return formatSingleDate(end)
  }

  if (
    start.year() === end!.year() &&
    start.month() === end!.month() &&
    start.date() === end!.date()
  ) {
    return `${formatSingleDate(start)} - ${end!.locale(locale).format('LT')}`
  }

  return `${formatSingleDate(start)} - ${formatSingleDate(end!)}`
}
